/**
 * calibrateGaze.js
 *
 * Loads MediaPipe FaceMesh, samples landmarks from a live video element
 * for `durationMs` milliseconds, and resolves with a calibration object.
 *
 * Landmark indices (with refineLandmarks: true):
 *   468 = left iris centre
 *   473 = right iris centre
 *   1   = nose tip
 *   33  = left eye outer corner
 *   263 = right eye outer corner
 *   10  = forehead top  (for pitch)
 *   152 = chin bottom   (for pitch)
 */

const MEDIAPIPE_CDN = "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh";

// Singleton so we only initialise FaceMesh once per page load
let _faceMeshInstance = null;

async function getFaceMesh() {
  if (_faceMeshInstance) return _faceMeshInstance;

    // Load via script tag instead of dynamic import (bundler-safe)
  await new Promise((resolve, reject) => {
    if (window.FaceMesh) { resolve(); return; }
    const script = document.createElement("script");
    script.src = `${MEDIAPIPE_CDN}/face_mesh.js`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });


  const fm = new window.FaceMesh({
    locateFile: (f) => `${MEDIAPIPE_CDN}/${f}`,
  });

  fm.setOptions({
    maxNumFaces: 2,        // detect extra faces as a violation signal
    refineLandmarks: true, // enables iris landmarks 468 / 473
    minDetectionConfidence: 0.6,
    minTrackingConfidence: 0.6,
  });

  await fm.initialize();
  _faceMeshInstance = fm;
  return fm;
}

/**
 * dist2D — Euclidean distance between two {x,y} landmarks (normalised 0-1)
 */
function dist2D(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * mean — arithmetic mean of an array of numbers
 */
function mean(arr) {
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

/**
 * stddev — population standard deviation
 */
function stddev(arr) {
  const m = mean(arr);
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
}

/**
 * calibrateGaze
 *
 * @param {HTMLVideoElement} videoEl   - live camera feed (must be playing)
 * @param {object}           [opts]
 * @param {number}           [opts.durationMs=5000]  - how long to sample
 * @param {function}         [opts.onProgress]       - called each frame with { elapsed, total, faceCount }
 *
 * @returns {Promise<CalibrationData>}
 *
 * CalibrationData shape:
 * {
 *   ok: true,
 *   neutralLeftIris:       { x, y },
 *   neutralRightIris:      { x, y },
 *   neutralYaw:            number,     // derived: (rightEye.x - leftEye.x) normalised diff
 *   neutralPitch:          number,     // derived: (noseTip.y - forehead.y) ratio
 *   interocularDistance:   number,     // normalised, used as scale reference
 *   gazeToleranceX:        number,     // 2.5σ of iris-X jitter during calibration
 *   gazeToleranceY:        number,     // 2.5σ of iris-Y jitter
 *   sampledAt:             number,     // Date.now() at completion
 *   frameCount:            number,
 * }
 *
 * On failure resolves with { ok: false, reason: string }
 */
export async function calibrateGaze(videoEl, { durationMs = 5000, onProgress } = {}) {
  // ── Collect raw samples ──────────────────────────────────────────────────
  const samples = {
    leftIrisX:  [],
    leftIrisY:  [],
    rightIrisX: [],
    rightIrisY: [],
    yaw:        [],
    pitch:      [],
    iod:        [],   // interocular distance
  };

  let faceMesh;
  try {
    faceMesh = await getFaceMesh();
  } catch (err) {
    return { ok: false, reason: "mediapipe_load_failed" };
  }

  const startTime = performance.now();

  await new Promise((resolve, reject) => {
    // Wire up the results handler
    faceMesh.onResults((results) => {
      const elapsed = performance.now() - startTime;
      const faces   = results.multiFaceLandmarks ?? [];

      onProgress?.({ elapsed, total: durationMs, faceCount: faces.length });

      if (faces.length === 1) {
        const lm = faces[0];

        const leftIris  = lm[468];
        const rightIris = lm[473];
        const leftEye   = lm[33];
        const rightEye  = lm[263];
        const noseTip   = lm[1];
        const forehead  = lm[10];

        samples.leftIrisX.push(leftIris.x);
        samples.leftIrisY.push(leftIris.y);
        samples.rightIrisX.push(rightIris.x);
        samples.rightIrisY.push(rightIris.y);

        // Yaw proxy: horizontal offset of left-eye vs right-eye, signed
        samples.yaw.push(rightEye.x - leftEye.x);

        // Pitch proxy: nose position relative to vertical eye-to-chin span
        samples.pitch.push(noseTip.y - forehead.y);

        // Interocular distance (scale reference)
        samples.iod.push(dist2D(leftEye, rightEye));
      }

      if (elapsed >= durationMs) {
        resolve();
      }
    });

    // Drive MediaPipe with requestAnimationFrame
    let rafId;
    const tick = async () => {
      if (performance.now() - startTime >= durationMs) {
        cancelAnimationFrame(rafId);
        resolve();
        return;
      }
      try {
        await faceMesh.send({ image: videoEl });
      } catch (_) {
        // drop frame on transient error
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    // Safety timeout — resolve even if RAF stalls
    setTimeout(() => { cancelAnimationFrame(rafId); resolve(); }, durationMs + 2000);
  });

  // ── Validate we got enough frames ────────────────────────────────────────
  const frameCount = samples.leftIrisX.length;

  if (frameCount < 10) {
    return {
      ok: false,
      reason: frameCount === 0 ? "no_face_detected" : "too_few_frames",
    };
  }

  // ── Compute calibration values ───────────────────────────────────────────
  const neutralLeftIris  = { x: mean(samples.leftIrisX),  y: mean(samples.leftIrisY)  };
  const neutralRightIris = { x: mean(samples.rightIrisX), y: mean(samples.rightIrisY) };

  // Tolerance = 2.5 standard deviations of natural jitter during calibration
  // Average left+right iris X/Y for a single tolerance figure
  const allIrisX = [...samples.leftIrisX, ...samples.rightIrisX];
  const allIrisY = [...samples.leftIrisY, ...samples.rightIrisY];

  const gazeToleranceX = Math.max(stddev(allIrisX) * 2.5, 0.03); // floor at 0.03
  const gazeToleranceY = Math.max(stddev(allIrisY) * 2.5, 0.03);

  return {
    ok:                  true,
    neutralLeftIris,
    neutralRightIris,
    neutralYaw:          mean(samples.yaw),
    neutralPitch:        mean(samples.pitch),
    interocularDistance: mean(samples.iod),
    gazeToleranceX,
    gazeToleranceY,
    sampledAt:           Date.now(),
    frameCount,
  };
}